import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import ImageGallery from 'react-image-gallery';
import "react-image-gallery/styles/css/image-gallery.css";
import './App.css';
import { useSwipeable } from 'react-swipeable';

// How many items to load into the gallery / grid at once. Keeps thumbnail
// requests from stampeding the CDN — each "load more" batch adds PAGE_SIZE.
const PAGE_SIZE = 60;

// localStorage keys for view-mode persistence.
const LS_VIEW_MODE = 'doetell_view_mode'; // 'grid' | 'single' | 'insights' | 'game'

// localStorage key for best streak in the Guess the Time game.
const LS_GAME_BEST = 'doetell_game_best';

// localStorage key for favorites (E1) — JSON array of sourceUrl strings.
const LS_FAVORITES = 'doetell_favorites';

// localStorage key for last-visit timestamp (E3) — numeric ms string.
const LS_LAST_VISIT = 'doetell_last_visit';

// Categories with fewer than this many total items (across all filters/dates)
// are considered "rare" for the E5 badge. Computed against the raw categoryMap
// so rarity is stable regardless of active filters.
const RARE_THRESHOLD = 25;

// Hour boundaries for day/night classification (S5).
// "Night" is defined as 20:00–04:59 UTC (8pm–5am), matching common wildlife
// nocturnal windows. Everything else is "day."
const NIGHT_START = 20; // 8pm inclusive
const NIGHT_END = 5;    // 5am exclusive (i.e. hour < 5 = night)

// Time-of-day band labels derived from capture hour (S4 / H3).
// Bands: night(0-4), dawn(5-7), morning(8-11), afternoon(12-16), dusk(17-19), night(20-23).
function hourBand(hour) {
  if (hour < 5)  return 'night';
  if (hour < 8)  return 'dawn';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 20) return 'dusk';
  return 'night';
}

// Display metadata for each time-of-day band, used by the Guess the Time game.
const BAND_INFO = {
  night:     { label: 'NIGHT',     range: '8pm – 5am',  accent: '#4a88d8' },
  dawn:      { label: 'DAWN',      range: '5am – 8am',  accent: '#f07820' },
  morning:   { label: 'MORNING',   range: '8am – noon', accent: '#d4b010' },
  afternoon: { label: 'AFTERNOON', range: 'noon – 5pm', accent: '#c0b825' },
  dusk:      { label: 'DUSK',      range: '5pm – 8pm',  accent: '#a858d8' },
};

// Returns true if the capture hour falls in the night window (S5).
function isNightHour(hour) {
  return hour >= NIGHT_START || hour < NIGHT_END;
}

// ─── Reduced-motion helper ────────────────────────────────────────────────────
// Read once at module level; components reference this to pick behavior.
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ─── Feather-style inline SVG icons ──────────────────────────────────────────

const Icon = ({ children }) => (
  <svg
    width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);
const IconPrev = () => <Icon><polyline points="15 18 9 12 15 6" /></Icon>;
const IconNext = () => <Icon><polyline points="9 18 15 12 9 6" /></Icon>;
const IconShuffle = () => (
  <Icon>
    <polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" />
    <polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" />
    <line x1="4" y1="4" x2="9" y2="9" />
  </Icon>
);
const IconExpand = () => (
  <Icon>
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </Icon>
);
const IconLink = () => (
  <Icon>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </Icon>
);
const IconCheck = () => <Icon><polyline points="20 6 9 17 4 12" /></Icon>;
const IconFlag = () => (
  <Icon>
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </Icon>
);
// Grid/Single view toggle icons.
const IconGrid = () => (
  <Icon>
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
  </Icon>
);
const IconSingle = () => (
  <Icon>
    <rect x="3" y="3" width="18" height="18" rx="1" />
  </Icon>
);
// Insights / bar-chart icon.
const IconInsights = () => (
  <Icon>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </Icon>
);
// Guess the Time game icon.
const IconGame = () => (
  <Icon>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Icon>
);
// Play chevron used as video overlay affordance in the grid.
const IconPlay = () => (
  <svg
    width="28" height="28" viewBox="0 0 24 24" fill="currentColor"
    stroke="none" aria-hidden="true"
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);
// Close X icon for filter chips.
const IconX = () => (
  <svg
    width="12" height="12" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
// E1: Heart icon for favorites toggle.
const IconHeart = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
// Sh3: Download icon.
const IconDownload = () => (
  <Icon>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </Icon>
);
// Sh2: Share icon.
const IconShare = () => (
  <Icon>
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </Icon>
);
// Sh4: Compare icon.
const IconCompare = () => (
  <Icon>
    <rect x="3" y="3" width="8" height="18" rx="1" />
    <rect x="13" y="3" width="8" height="18" rx="1" />
  </Icon>
);

// ─── Skeleton grid shown while data loads (G1) ───────────────────────────────

const SkeletonGrid = () => (
  <div className="skeleton-grid" aria-hidden="true">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="skeleton-tile" />
    ))}
  </div>
);

// ─── Focus trap hook (F5) ────────────────────────────────────────────────────
// Returns a ref to attach to the container. While mounted, Tab and Shift+Tab
// cycle only through focusable elements inside that container.

function useFocusTrap(active) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const el = containerRef.current;
    if (!el) return;

    // Focus the first focusable element on open.
    const focusable = el.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) focusable[0].focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const all = Array.from(
        el.querySelectorAll(
          'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!all.length) { e.preventDefault(); return; }
      const first = all[0];
      const last = all[all.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);

  return containerRef;
}

// ─── Insights helpers ─────────────────────────────────────────────────────────

// Format a UTC epoch as "Jan 2025", "Oct 3 2025", etc.
function fmtMonthYear(ts) {
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', timeZone: 'UTC',
  });
}
function fmtFullDate(ts) {
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}
// Format hour-of-day as 12h with am/pm suffix.
function fmtHour(h) {
  if (h === 0) return '12am';
  if (h < 12) return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}
// Format capture time as "5:42am" for the single-view toolbar (S4).
function fmtCaptureTime(ts) {
  const d = new Date(ts);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const mm = m < 10 ? `0${m}` : `${m}`;
  const ampm = h < 12 ? 'am' : 'pm';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm}${ampm}`;
}

// Capitalize each word in a category name ("other animal" → "Other Animal").
function formatLabel(cat) {
  return cat.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ─── SVG Bar Chart component (S2 — month trend) ───────────────────────────────

function MonthBarChart({ data, label }) {
  // data: [{ key: 'Jan 2025', count: N }, ...]
  if (!data || data.length === 0) {
    return <p className="insights-empty">No timestamped items in this range.</p>;
  }
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const barW = Math.max(18, Math.min(40, Math.floor(560 / data.length) - 4));
  const chartW = data.length * (barW + 4);
  const chartH = 120;
  const labelH = 36;
  const totalH = chartH + labelH;

  return (
    <div className="chart-scroll-wrapper" role="img" aria-label={`Monthly trend chart: ${label}`}>
      <svg
        width={Math.max(chartW, 280)}
        height={totalH}
        className="month-bar-chart"
        aria-hidden="true"
      >
        {data.map((d, i) => {
          const barH = Math.max(2, Math.round((d.count / maxVal) * chartH));
          const x = i * (barW + 4);
          const y = chartH - barH;
          const isNight = false; // month bars don't need night coloring
          return (
            <g key={d.key}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={barH}
                rx="1"
                className="chart-bar"
              />
              {/* Count label above bar — only for wider bars */}
              {barW >= 24 && d.count > 0 && (
                <text
                  x={x + barW / 2}
                  y={y - 3}
                  textAnchor="middle"
                  className="chart-bar-count"
                >
                  {d.count}
                </text>
              )}
              {/* Month label below — rotated for narrow bars */}
              <text
                x={x + barW / 2}
                y={chartH + 14}
                textAnchor="middle"
                className="chart-axis-label"
                transform={barW < 28 ? `rotate(-45,${x + barW / 2},${chartH + 14})` : undefined}
              >
                {d.key}
              </text>
            </g>
          );
        })}
        {/* Baseline */}
        <line x1="0" y1={chartH} x2={Math.max(chartW, 280)} y2={chartH} className="chart-baseline" />
      </svg>
    </div>
  );
}

// ─── SVG Heatmap row (S1 — activity by hour) ─────────────────────────────────

function HourHeatmap({ hourCounts, peakHour }) {
  // hourCounts: array[24] of counts
  const maxVal = Math.max(...hourCounts, 1);
  const cellSize = 28; // px per cell
  const totalW = 24 * (cellSize + 2);

  const hasData = hourCounts.some((c) => c > 0);
  if (!hasData) {
    return <p className="insights-empty">No timestamped items to chart.</p>;
  }

  return (
    <div className="heatmap-wrapper">
      <div
        className="heatmap-grid"
        role="img"
        aria-label="Activity by hour of day heatmap"
      >
        {hourCounts.map((count, h) => {
          const intensity = maxVal > 0 ? count / maxVal : 0;
          const isNight = isNightHour(h);
          const isPeak = h === peakHour && count > 0;
          return (
            <div
              key={h}
              className={`heatmap-cell ${isNight ? 'heatmap-cell-night' : ''} ${isPeak ? 'heatmap-cell-peak' : ''}`}
              style={{ '--intensity': intensity }}
              title={`${fmtHour(h)}: ${count} item${count !== 1 ? 's' : ''}`}
              aria-label={`${fmtHour(h)}: ${count} item${count !== 1 ? 's' : ''}`}
            >
              <span className="heatmap-hour-label">{fmtHour(h)}</span>
              <span className="heatmap-count-label">{count > 0 ? count : ''}</span>
            </div>
          );
        })}
      </div>
      <div className="heatmap-legend">
        <span className="heatmap-legend-item heatmap-legend-day">DAY</span>
        <span className="heatmap-legend-item heatmap-legend-night">NIGHT</span>
        {peakHour !== null && hourCounts[peakHour] > 0 && (
          <span className="heatmap-legend-peak">
            Peak around {fmtHour(peakHour)} ({hourBand(peakHour)})
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Sh2: Shareable highlight card modal ─────────────────────────────────────
// Defined at module level (outside App) so it doesn't re-mount on every render.

function ShareCardModal({ item, onClose }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!item || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1080;

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Dark background
      ctx.fillStyle = '#0a0a0a';
      ctx.fillRect(0, 0, 1080, 1080);
      // Draw photo centered, cover-style
      const s = Math.max(1080 / img.naturalWidth, 1080 / img.naturalHeight);
      const sw = img.naturalWidth * s;
      const sh = img.naturalHeight * s;
      ctx.drawImage(img, (1080 - sw) / 2, (1080 - sh) / 2, sw, sh);
      // Gradient overlay at bottom
      const grad = ctx.createLinearGradient(0, 600, 0, 1080);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,0.82)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1080, 1080);
      // Category + date text
      ctx.fillStyle = '#b8922f';
      ctx.font = 'bold 36px "Oswald", Impact, sans-serif';
      ctx.letterSpacing = '0.12em';
      const catText = item.category ? item.category.toUpperCase() : 'WILDLIFE';
      const dateText = item.timestamp
        ? new Date(item.timestamp).toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
          })
        : '';
      ctx.fillText(catText, 48, 940);
      ctx.fillStyle = '#d4cfc6';
      ctx.font = '28px "Inter", system-ui, sans-serif';
      ctx.fillText(dateText, 48, 984);
      // Branding
      ctx.fillStyle = '#f0ebe2';
      ctx.font = 'bold 28px "Oswald", Impact, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('DOE & TELL', 1080 - 48, 984);
      ctx.textAlign = 'left';
      setReady(true);
    };
    img.onerror = () =>
      setError('Could not load image for share card — CORS may not be enabled on this bucket.');
    // Use the display rendition for the card
    img.src = item.original || item.sourceUrl;
  }, [item]);

  const download = () => {
    if (!canvasRef.current || !ready) return;
    try {
      const a = document.createElement('a');
      a.download = `doetell-${item?.category || 'wildlife'}-${Date.now()}.png`;
      a.href = canvasRef.current.toDataURL('image/png');
      a.click();
    } catch (e) {
      setError('Could not generate download — this is likely a CORS issue with the image source.');
    }
  };

  return (
    <div className="flag-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="flag-modal share-card-modal"
        role="dialog"
        aria-labelledby="share-card-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="share-card-title" className="flag-modal-title">Share Card</h2>
        {error ? (
          <p className="flag-modal-error">{error}</p>
        ) : (
          <canvas
            ref={canvasRef}
            className="share-card-canvas"
            aria-label="Shareable highlight card preview"
          />
        )}
        {!error && !ready && <p className="insights-empty">Generating…</p>}
        <div className="flag-modal-actions">
          <button type="button" className="toolbar-btn" onClick={onClose}>Close</button>
          {ready && !error && (
            <button type="button" className="toolbar-btn toolbar-btn-accent" onClick={download}>
              DOWNLOAD PNG
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  // ── Data state ─────────────────────────────────────────────────────────────
  const [categoryMap, setCategoryMap] = useState({});
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loadError, setLoadError] = useState(false); // G2: explicit error state

  // ── Filter / sort state ────────────────────────────────────────────────────
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all' | 'pictures' | 'videos'
  // Date range: preset is 'all' | '7d' | '30d' | 'year' | 'custom'.
  // customFrom/customTo are 'YYYY-MM-DD' strings from the date inputs.
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState(''); // applied range (drives filtering)
  const [customTo, setCustomTo] = useState('');
  const [draftFrom, setDraftFrom] = useState(''); // staged in the inputs; not applied until Apply
  const [draftTo, setDraftTo] = useState('');
  const [sortDir, setSortDir] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── View / display state ───────────────────────────────────────────────────
  // 'grid' = contact-sheet (default, persisted to localStorage).
  // 'single' = the existing react-image-gallery carousel.
  // 'insights' = the new stats / data-insights panel.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_VIEW_MODE);
      // 'insights' is not persisted as the default; fall back to 'grid'.
      return (stored === 'grid' || stored === 'single') ? stored : 'grid';
    } catch { return 'grid'; }
  });

  // How many grid items are currently visible (grows as user scrolls).
  // Reset to PAGE_SIZE whenever the filter/sort/category changes.
  const [gridVisible, setGridVisible] = useState(PAGE_SIZE);

  // Tracks the grid's scroll position so it can be restored when the user
  // returns from single view. Stored as a window.scrollY snapshot.
  const gridScrollRef = useRef(0);

  // ── Single-view (carousel) state ───────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── Insights state ─────────────────────────────────────────────────────────
  // Which species selector is active in the heatmap (null = all / active category).
  const [heatmapSpecies, setHeatmapSpecies] = useState(null);
  // Day/night filter: null | 'day' | 'night' (S5 quick-filter).
  const [dayNightFilter, setDayNightFilter] = useState(null);

  // ── Guess the Time game ───────────────────────────────────────────────────
  const [gameItem, setGameItem] = useState(null);
  const [gameGuess, setGameGuess] = useState(null); // null = not yet guessed
  const [gameStreak, setGameStreak] = useState(0);
  const [gameBestStreak, setGameBestStreak] = useState(() => {
    try { return parseInt(localStorage.getItem(LS_GAME_BEST) || '0', 10) || 0; } catch { return 0; }
  });

  // ── E1: Favorites ─────────────────────────────────────────────────────────
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_FAVORITES);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // ── E3: New since last visit ───────────────────────────────────────────────
  const [lastVisitTs, setLastVisitTs] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_LAST_VISIT);
      return raw ? Number(raw) : null;
    } catch { return null; }
  });
  const [showNewOnly, setShowNewOnly] = useState(false);

  // ── D5: On this day ────────────────────────────────────────────────────────
  const [onThisDay, setOnThisDay] = useState(false);

  // ── Sh2: Share card ────────────────────────────────────────────────────────
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const [shareCardItem, setShareCardItem] = useState(null);

  // ── C2/Sh1: Deep-link copy state ──────────────────────────────────────────
  const [copiedLink, setCopiedLink] = useState(false);

  // ── Sh4: Compare mode ─────────────────────────────────────────────────────
  const [compareMode, setCompareMode] = useState(false);
  const [compareItems, setCompareItems] = useState([]); // up to 2 items

  // ── Misc UI state ──────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagSuggested, setFlagSuggested] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagError, setFlagError] = useState(null);
  const [flagToast, setFlagToast] = useState(null);

  // F1: track which grid tile index the user clicked so focus can return to it.
  const lastGridFocusIdx = useRef(null);
  // Refs for individual grid tile buttons (map from idx → DOM node).
  const gridTileRefs = useRef({});
  // Ref for the flag-button in the toolbar so focus can return after modal close.
  const flagBtnRef = useRef(null);
  // Ref for the single-view container so focus can land there on open.
  const singleViewRef = useRef(null);
  // C2/Sh1: pending deep-link item URL to open after allItems is computed.
  const pendingDeepLinkItem = useRef(null);

  // Sentinel element at the bottom of the grid; watched by IntersectionObserver
  // to trigger "load more" batches automatically.
  const gridSentinelRef = useRef(null);

  const galleryRef = useRef(null);

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => {
    // Prefer the precomputed static index (served from the CDN in ~300ms);
    // fall back to the Lambda API (re-lists the bucket, ~5s) if it's missing
    // (e.g. local dev, or before the first refresh).
    const loadFromIndex = async () => {
      const res = await fetch('/images/index.json', { cache: 'no-cache' });
      if (!res.ok) throw new Error(`index ${res.status}`);
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('json')) throw new Error('index not json');
      return res.json();
    };
    const loadFromApi = async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      return res.json();
    };

    loadFromIndex()
      .catch(() => loadFromApi())
      .then(grouped => {
        const keys = Object.keys(grouped).sort();
        setCategoryMap(grouped);
        setCategories(keys);
        // C2/Sh1: Parse search params for deep-linking (replaces hash-based nav).
        const params = new URLSearchParams(window.location.search);
        const pCat = params.get('cat');
        const pSort = params.get('sort');
        const pMedia = params.get('media');
        const pItem = params.get('item');
        if (pSort === 'asc' || pSort === 'desc') setSortDir(pSort);
        if (pMedia === 'pictures' || pMedia === 'videos') setMediaFilter(pMedia);
        if (pCat === 'all') setActiveCategory('all');
        else if (pCat && keys.includes(pCat)) setActiveCategory(pCat);
        else {
          // Fall back to legacy hash-based nav for backward compat.
          const h = window.location.hash.slice(1);
          if (h) {
            const decoded = decodeURIComponent(h);
            if (decoded === 'all') setActiveCategory('all');
            else if (keys.includes(decoded)) setActiveCategory(decoded);
          }
        }
        if (pItem) pendingDeepLinkItem.current = decodeURIComponent(pItem);
      })
      // G2: on total failure show an explicit error state instead of blank page.
      .catch(error => {
        console.error('Error loading images:', error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // E3: Write last-visit timestamp on page unload so next session sees new items.
  useEffect(() => {
    const handler = () => {
      try { localStorage.setItem(LS_LAST_VISIT, String(Date.now())); } catch {}
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ── Memoized helpers ───────────────────────────────────────────────────────

  // Memoized so the <video> render fn keeps a stable identity across renders
  // (otherwise the gallery sees "new" items every render and desyncs its index).
  const renderVideo = useCallback((item) => (
    <div className="image-gallery-image">
      <video
        className="gallery-video"
        src={item.sourceUrl}
        poster={item.original}
        controls
        playsInline
        preload="metadata"
      />
      {item.category && activeCategory === 'all' && (
        <span className="video-category-badge">{formatLabel(item.category)}</span>
      )}
    </div>
  ), [activeCategory]);

  const isVideoEntry = (entry) => typeof entry === 'object' && entry?.type === 'video';

  // Resolve the active preset/custom selection into an inclusive [from, to]
  // epoch-ms window. null bounds mean "unbounded" on that side.
  const dateWindow = useMemo(() => {
    // Bounds are computed in UTC to match how capture times are stored.
    if (datePreset === 'custom') {
      const from = customFrom ? new Date(`${customFrom}T00:00:00Z`).getTime() : null;
      const to = customTo ? new Date(`${customTo}T23:59:59.999Z`).getTime() : null;
      return { from, to };
    }
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    if (datePreset === '7d') return { from: now - 7 * DAY, to: null };
    if (datePreset === '30d') return { from: now - 30 * DAY, to: null };
    if (datePreset === 'year') {
      return { from: Date.UTC(new Date().getUTCFullYear(), 0, 1), to: null };
    }
    return { from: null, to: null }; // 'all'
  }, [datePreset, customFrom, customTo]);

  // ── E5: Rare category set ──────────────────────────────────────────────────
  // Computed from the raw categoryMap (not filtered) so rarity is stable
  // regardless of active date/media filters. A category is "rare" if it has
  // fewer than RARE_THRESHOLD total items.
  const rareCategories = useMemo(() => {
    const rare = new Set();
    for (const cat of categories) {
      const total = (categoryMap[cat] || []).length;
      if (total > 0 && total < RARE_THRESHOLD) rare.add(cat);
    }
    return rare;
  }, [categories, categoryMap]);

  // Build the full item list for the active view, applying the media-type and
  // date filters, sorted newest-first. Memoized on its real inputs so
  // navigating (which updates currentSlide) doesn't rebuild item identities.
  const allItems = useMemo(() => {
    // F2: Build meaningful alt text from category + timestamp (UTC).
    const buildAlt = (category, timestamp, isVideo) => {
      const catLabel = category ? formatLabel(category) : 'Wildlife';
      const dateStr = timestamp
        ? new Date(timestamp).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
          })
        : '';
      const mediaWord = isVideo ? 'video' : 'photo';
      return dateStr
        ? `${catLabel} ${mediaWord} — ${dateStr}`
        : `${catLabel} ${mediaWord}`;
    };

    const toItem = (entry, category) => {
      if (typeof entry === 'string') {
        return {
          original: entry, thumbnail: entry, sourceUrl: entry, category,
          timestamp: 0, originalAlt: buildAlt(category, 0, false),
          thumbnailAlt: buildAlt(category, 0, false),
        };
      }
      const timestamp = entry.timestamp || 0;
      if (entry.type === 'video') {
        const poster = entry.poster || entry.url;
        const alt = buildAlt(category, timestamp, true);
        return {
          original: poster,
          thumbnail: poster,
          sourceUrl: entry.url,
          type: 'video',
          category,
          timestamp,
          renderItem: renderVideo,
          originalAlt: alt,
          thumbnailAlt: alt,
        };
      }
      // Light display rendition for the main view, tiny thumbnail for the strip
      // and the grid, and the full-res original only for fullscreen / copy-link.
      const alt = buildAlt(category, timestamp, false);
      return {
        original: entry.display || entry.url,
        thumbnail: entry.thumbnail || entry.url,
        fullscreen: entry.url,
        sourceUrl: entry.url,
        category,
        timestamp,
        originalAlt: alt,
        thumbnailAlt: alt,
      };
    };

    const passesMedia = (entry) => {
      if (mediaFilter === 'videos') return isVideoEntry(entry);
      if (mediaFilter === 'pictures') return !isVideoEntry(entry);
      return true;
    };

    const { from, to } = dateWindow;
    const passesDate = (entry) => {
      if (from === null && to === null) return true;
      const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
      if (from !== null && ts < from) return false;
      if (to !== null && ts > to) return false;
      return true;
    };

    // S5: day/night filter applied here so grid + single view both respect it.
    const passesDayNight = (entry) => {
      if (!dayNightFilter) return true;
      const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
      if (!ts) return false; // items with no timestamp are excluded when filtering
      const hour = new Date(ts).getUTCHours();
      return dayNightFilter === 'night' ? isNightHour(hour) : !isNightHour(hour);
    };

    // D5: on-this-day filter — matches items whose UTC month+day equals today's.
    const passesOnThisDay = (entry) => {
      if (!onThisDay) return true;
      const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
      if (!ts) return false;
      const d = new Date(ts);
      const today = new Date();
      return d.getUTCMonth() === today.getUTCMonth() && d.getUTCDate() === today.getUTCDate();
    };

    const cats = activeCategory === 'all' ? categories : [activeCategory];
    const items = [];
    for (const cat of cats) {
      for (const entry of categoryMap[cat] || []) {
        if (passesMedia(entry) && passesDate(entry) && passesDayNight(entry) && passesOnThisDay(entry))
          items.push(toItem(entry, cat));
      }
    }
    // Sort by capture time across all categories in the current view.
    items.sort((a, b) => (sortDir === 'asc' ? a.timestamp - b.timestamp : b.timestamp - a.timestamp));

    // E1: favorites filter (applied after sort so ordering is preserved).
    const afterFavs = showFavoritesOnly
      ? items.filter(item => favorites.has(item.sourceUrl))
      : items;

    // E3: new-since-last-visit filter (uses lastVisitTs primitive — no circular dep).
    const afterNew = showNewOnly && lastVisitTs
      ? afterFavs.filter(item => item.timestamp > lastVisitTs)
      : afterFavs;

    return afterNew;
  }, [categories, categoryMap, activeCategory, mediaFilter, dateWindow, sortDir, renderVideo, dayNightFilter, onThisDay, showFavoritesOnly, favorites, showNewOnly, lastVisitTs]);

  // E3: Set of sourceUrls for items newer than lastVisitTs — derived from allItems
  // (which already has the showNewOnly filter baked in; this set is used for badges).
  // We recompute from the unfiltered allItems so badges appear even when showNewOnly=false.
  // To avoid re-including allItems in its own memo, we compute this here as a separate memo.
  const newItemsSet = useMemo(() => {
    if (!lastVisitTs) return new Set();
    const s = new Set();
    for (const item of allItems) {
      if (item.timestamp && item.timestamp > lastVisitTs) s.add(item.sourceUrl);
    }
    return s;
  }, [allItems, lastVisitTs]);

  // C3: Year-month groups for the date-jump rail, derived from allItems order.
  const dateGroups = useMemo(() => {
    const seen = new Map(); // 'YYYY-MM' → { key, label, firstIdx }
    allItems.forEach((item, idx) => {
      if (!item.timestamp) return;
      const d = new Date(item.timestamp);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          label: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', timeZone: 'UTC' }),
          firstIdx: idx,
        });
      }
    });
    return [...seen.values()];
  }, [allItems]);

  // The slice of allItems exposed to the carousel (still page-based for single
  // view to keep the gallery from firing thousands of simultaneous requests).
  const totalPages = Math.max(1, Math.ceil(allItems.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const images = useMemo(
    () => allItems.slice(pageStart, pageStart + PAGE_SIZE),
    [allItems, pageStart],
  );

  // The slice of allItems visible in the grid (continuous, growing with scroll).
  const gridItems = useMemo(
    () => allItems.slice(0, gridVisible),
    [allItems, gridVisible],
  );

  // ── Reset logic ────────────────────────────────────────────────────────────

  // Any filter/category/sort change resets both views to the beginning.
  useLayoutEffect(() => {
    setCurrentSlide(0);
    setPage(0);
    setGridVisible(PAGE_SIZE);
    gridScrollRef.current = 0;
    // Clear tile ref cache on reset to avoid stale keys.
    gridTileRefs.current = {};
  }, [activeCategory, mediaFilter, dateWindow, sortDir, dayNightFilter]);

  // Current item in single view (for S4 time-of-day context + E5 rare badge).
  const currentItem = images[currentSlide] || null;
  const currentItemHour = currentItem?.timestamp
    ? new Date(currentItem.timestamp).getUTCHours()
    : null;
  const currentItemBand = currentItemHour !== null ? hourBand(currentItemHour) : null;
  const currentItemIsRare = currentItem?.category
    ? rareCategories.has(currentItem.category)
    : false;

  // ── View-mode persistence ──────────────────────────────────────────────────

  const setAndPersistViewMode = useCallback((mode) => {
    setViewMode(mode);
    // Don't persist 'insights' as the default — grid/single are the primary views.
    if (mode === 'grid' || mode === 'single') {
      try { localStorage.setItem(LS_VIEW_MODE, mode); } catch { /* ignore */ }
    }
  }, []);

  // ── Grid tile click → open single view at that item ───────────────────────

  const openGridItem = useCallback((absoluteIdx) => {
    // Save scroll position so we can restore it when returning to grid.
    gridScrollRef.current = window.scrollY;
    // Save which tile was clicked so we can return focus to it (F1).
    lastGridFocusIdx.current = absoluteIdx;

    const targetPage = Math.floor(absoluteIdx / PAGE_SIZE);
    const targetSlide = absoluteIdx % PAGE_SIZE;
    setPage(targetPage);
    setCurrentSlide(targetSlide);
    setAndPersistViewMode('single');

    // Scroll to top so the focused image is immediately visible.
    // F4: respect reduced-motion for scroll behavior.
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });

    // Give the gallery a frame to mount before sliding.
    requestAnimationFrame(() => {
      const g = galleryRef.current;
      if (g) g.slideToIndex(targetSlide);
    });
  }, [setAndPersistViewMode]);

  // Returning from single → grid: restore the saved scroll position and focus.
  const returnToGrid = useCallback(() => {
    setAndPersistViewMode('grid');
    const savedY = gridScrollRef.current;
    const focusIdx = lastGridFocusIdx.current;
    // Use rAF so the grid has rendered before we scroll and restore focus.
    requestAnimationFrame(() => {
      window.scrollTo({ top: savedY, behavior: 'instant' });
      // F1: restore focus to the originating tile.
      if (focusIdx !== null && gridTileRefs.current[focusIdx]) {
        gridTileRefs.current[focusIdx].focus();
      }
    });
  }, [setAndPersistViewMode]);

  // ── C2/Sh1: URL search-params sync (replaces old hash-based sync) ────────

  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams();
    params.set('cat', activeCategory);
    if (sortDir !== 'desc') params.set('sort', sortDir);
    if (mediaFilter !== 'all') params.set('media', mediaFilter);
    if (viewMode === 'single' && currentItem?.sourceUrl)
      params.set('item', encodeURIComponent(currentItem.sourceUrl));
    const next = `?${params.toString()}`;
    if (window.location.search !== next)
      window.history.replaceState(null, '', next);
  }, [activeCategory, sortDir, mediaFilter, viewMode, currentItem, loading]);

  // C2/Sh1: After allItems settles, consume any pending deep-link item URL.
  useEffect(() => {
    if (!pendingDeepLinkItem.current || allItems.length === 0) return;
    const targetUrl = pendingDeepLinkItem.current;
    pendingDeepLinkItem.current = null; // consume once
    const idx = allItems.findIndex(item => item.sourceUrl === targetUrl);
    if (idx !== -1) openGridItem(idx);
  }, [allItems, openGridItem]);

  // ── IntersectionObserver for grid infinite scroll ──────────────────────────

  useEffect(() => {
    // Only wire the observer when in grid mode and there are more items to show.
    if (viewMode !== 'grid') return;
    if (gridVisible >= allItems.length) return;

    const sentinel = gridSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Append another PAGE_SIZE batch without exceeding total.
          setGridVisible((v) => Math.min(v + PAGE_SIZE, allItems.length));
        }
      },
      { rootMargin: '200px' }, // start loading slightly before the sentinel enters view
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, gridVisible, allItems.length]);

  // ── Single-view carousel helpers ───────────────────────────────────────────

  const slideByDelta = useCallback((delta) => {
    const g = galleryRef.current;
    if (!g || images.length === 0) return;
    const idx = g.getCurrentIndex();
    const next = Math.max(0, Math.min(images.length - 1, idx + delta));
    g.slideToIndex(next);
  }, [images.length]);

  // ── F1: Arrow-key navigation in single view ────────────────────────────────

  useEffect(() => {
    if (viewMode !== 'single') return;
    const handleKey = (e) => {
      // Only fire when focus is inside the single view or on the body (not in an input).
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        slideByDelta(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        slideByDelta(1);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [viewMode, images.length, slideByDelta]);

  // ── F1: Move focus into single view on open ────────────────────────────────

  useEffect(() => {
    if (viewMode === 'single' && singleViewRef.current) {
      // Give the gallery a frame to mount, then focus the container.
      requestAnimationFrame(() => {
        singleViewRef.current?.focus();
      });
    }
  }, [viewMode]);

  const handlers = useSwipeable({
    onSwipedLeft: () => slideByDelta(1),
    onSwipedRight: () => slideByDelta(-1),
  });

  const goPrev = () => slideByDelta(-1);
  const goNext = () => slideByDelta(1);

  // ── True full-collection "Surprise me" (A3 / D2 / H1) ─────────────────────
  // Picks a random item from ALL filtered items (not just the current page),
  // switches to single view, jumps to the correct page, and slides to that item.
  const goRandom = useCallback(() => {
    if (allItems.length <= 1) return;

    // Identify the absolute index of the currently focused item so we never
    // repeat it. In grid mode we don't have a focused item, so -1.
    const currentAbsoluteIdx =
      viewMode === 'single' ? pageStart + currentSlide : -1;

    let nextAbsolute;
    do {
      nextAbsolute = Math.floor(Math.random() * allItems.length);
    } while (nextAbsolute === currentAbsoluteIdx);

    // Compute which page that item lands on in single/carousel mode.
    const targetPage = Math.floor(nextAbsolute / PAGE_SIZE);
    const targetSlide = nextAbsolute % PAGE_SIZE;

    // Always open in single view so the chosen item is focused.
    setAndPersistViewMode('single');
    setPage(targetPage);
    setCurrentSlide(targetSlide);

    // After the state settles, tell the gallery to jump there. We use
    // requestAnimationFrame to let React flush the page change first.
    requestAnimationFrame(() => {
      const g = galleryRef.current;
      if (g) g.slideToIndex(targetSlide);
    });
  }, [allItems.length, viewMode, pageStart, currentSlide, setAndPersistViewMode]);

  // ── Guess the Time game logic ──────────────────────────────────────────────

  const pickGameItem = useCallback(() => {
    // Draw from the full unfiltered archive (photos only; video posters are ambiguous).
    // Raw categoryMap entries use `url`, not `sourceUrl` (toItem() adds sourceUrl later).
    const pool = Object.values(categoryMap).flat().filter(
      item => item.timestamp && item.type !== 'video'
    );
    if (!pool.length) return;
    let pick;
    do {
      pick = pool[Math.floor(Math.random() * pool.length)];
    } while (pool.length > 1 && gameItem && pick.url === gameItem.url);
    setGameItem(pick);
    setGameGuess(null);
  }, [categoryMap, gameItem]);

  const handleGameGuess = useCallback((band) => {
    if (!gameItem?.timestamp || gameGuess !== null) return;
    const correct = hourBand(new Date(gameItem.timestamp).getUTCHours());
    const isCorrect = band === correct;
    const nextStreak = isCorrect ? gameStreak + 1 : 0;
    setGameGuess(band);
    setGameStreak(nextStreak);
    if (nextStreak > gameBestStreak) {
      setGameBestStreak(nextStreak);
      try { localStorage.setItem(LS_GAME_BEST, String(nextStreak)); } catch {}
    }
  }, [gameItem, gameGuess, gameStreak, gameBestStreak]);

  // Auto-pick the first item when entering game mode.
  useEffect(() => {
    if (viewMode === 'game' && !gameItem && Object.keys(categoryMap).length > 0) {
      pickGameItem();
    }
  }, [viewMode, gameItem, categoryMap, pickGameItem]);

  // ── Page navigation (single-view only) ────────────────────────────────────

  const goToPage = (p) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, p));
    setPage(clamped);
    setCurrentSlide(0);
    window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };
  const goPrevPage = () => goToPage(safePage - 1);
  const goNextPage = () => goToPage(safePage + 1);

  // ── E1: Favorites toggle ───────────────────────────────────────────────────

  const toggleFavorite = useCallback((sourceUrl) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(sourceUrl)) next.delete(sourceUrl);
      else next.add(sourceUrl);
      try { localStorage.setItem(LS_FAVORITES, JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  // ── Sh2: Open share card ───────────────────────────────────────────────────

  const openShareCard = useCallback((item) => {
    setShareCardItem(item);
    setShareCardOpen(true);
  }, []);

  // ── Action toolbar helpers ─────────────────────────────────────────────────

  const copyImageLink = async () => {
    const url = images[currentSlide]?.sourceUrl || images[currentSlide]?.original;
    if (!url || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  // C2/Sh1: Copy the current app URL (with search params) to clipboard.
  const copyViewLink = async () => {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 2000);
    } catch {}
  };

  // C3: Jump-to-date — ensure the target tile is loaded then scroll to it.
  const jumpToDate = useCallback((firstIdx) => {
    setGridVisible(prev => Math.max(prev, firstIdx + 1));
    // Retry scrollIntoView until the tile ref is mounted (it may not be on the
    // first rAF if React hasn't committed the newly-visible gridItems yet).
    const scrollToTile = () => {
      const el = gridTileRefs.current[firstIdx];
      if (el) el.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
      else requestAnimationFrame(scrollToTile);
    };
    requestAnimationFrame(scrollToTile);
  }, []);

  // Open the untouched full-resolution image in the browser's native image
  // viewer (new tab). That gives real pinch-zoom/rotate/save on mobile and
  // click-to-zoom/right-click-save on desktop — with no gallery chrome. The
  // heavy original is fetched only on demand. Videos keep their own controls.
  const openFullSize = () => {
    const item = images[currentSlide];
    if (!item || item.type === 'video') return;
    const url = item.sourceUrl || item.fullscreen || item.original;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Click/tap the photo opens the full-size image.
  const onImageClick = () => openFullSize();

  // Toolbar "view full size": full image for photos, native maximize for video.
  const viewFullSize = () => {
    if (images[currentSlide]?.type === 'video') maximizeVideo();
    else openFullSize();
  };

  const maximizeVideo = () => {
    const vid =
      document.querySelector('.image-gallery-slide.center video') ||
      document.querySelector('.image-gallery-slide video') ||
      document.querySelector('.gallery-video');
    if (!vid) return;
    if (vid.requestFullscreen) vid.requestFullscreen();
    else if (vid.webkitEnterFullscreen) vid.webkitEnterFullscreen(); // iOS Safari
    else if (vid.webkitRequestFullscreen) vid.webkitRequestFullscreen();
  };

  // ── Flag modal ─────────────────────────────────────────────────────────────

  const openFlagModal = () => {
    setFlagSuggested('');
    setFlagNote('');
    setFlagError(null);
    setFlagModalOpen(true);
  };

  const closeFlagModal = useCallback(() => {
    if (flagSubmitting) return;
    setFlagModalOpen(false);
    setFlagError(null);
    // F5: return focus to the flag button that opened the modal.
    requestAnimationFrame(() => {
      flagBtnRef.current?.focus();
    });
  }, [flagSubmitting]);

  // F5: Escape to close the flag modal.
  useEffect(() => {
    if (!flagModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeFlagModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [flagModalOpen, flagSubmitting, closeFlagModal]);

  // F5: Focus trap inside the flag modal.
  const flagModalRef = useFocusTrap(flagModalOpen);

  const submitFlag = async () => {
    const apiBase = process.env.REACT_APP_API_URL;
    const imageUrl = images[currentSlide]?.sourceUrl || images[currentSlide]?.original;
    if (!apiBase || !imageUrl) return;
    setFlagSubmitting(true);
    setFlagError(null);
    try {
      const body = { imageUrl };
      if (flagSuggested.trim()) body.suggestedCategory = flagSuggested.trim();
      if (flagNote.trim()) body.note = flagNote.trim();
      const res = await fetch(`${apiBase}/flags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFlagError(data.message || `Request failed (${res.status})`);
        return;
      }
      setFlagModalOpen(false);
      setFlagToast('Thanks — we will review this photo.');
      window.setTimeout(() => setFlagToast(null), 4000);
      // F5: return focus to flag button after successful submission.
      requestAnimationFrame(() => { flagBtnRef.current?.focus(); });
    } catch (e) {
      console.error(e);
      setFlagError('Could not reach the server. Try again later.');
    } finally {
      setFlagSubmitting(false);
    }
  };

  // ── Counts & labels ────────────────────────────────────────────────────────

  // Per-category counts that respect the active media-type and date filters, so
  // the tab badges reflect what's actually shown. `__all` is the grand total.
  const filteredCounts = useMemo(() => {
    const { from, to } = dateWindow;
    const passes = (entry) => {
      const isVid = typeof entry === 'object' && entry?.type === 'video';
      if (mediaFilter === 'videos' && !isVid) return false;
      if (mediaFilter === 'pictures' && isVid) return false;
      if (from !== null || to !== null) {
        const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
        if (from !== null && ts < from) return false;
        if (to !== null && ts > to) return false;
      }
      return true;
    };
    const counts = {};
    let total = 0;
    for (const cat of categories) {
      let n = 0;
      for (const entry of categoryMap[cat] || []) {
        if (passes(entry)) n++;
      }
      counts[cat] = n;
      total += n;
    }
    counts.__all = total;
    return counts;
  }, [categories, categoryMap, mediaFilter, dateWindow]);

  // Capture times are stored as wall-clock time read as UTC, so format in UTC
  // to show the date the camera recorded regardless of the viewer's timezone.
  const formatDate = (ts) =>
    ts
      ? new Date(ts).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : '';

  // ── Date filter helpers ────────────────────────────────────────────────────

  const applyPreset = useCallback((preset) => {
    setDatePreset(preset);
    setCustomFrom('');
    setCustomTo('');
    setDraftFrom('');
    setDraftTo('');
  }, []);

  // Commit the staged From/To so the filter only changes once the user is done
  // choosing dates (not while clicking through the calendar). The committed
  // range then persists across category/media changes until cleared or a preset
  // is chosen.
  const applyCustomRange = useCallback(() => {
    setCustomFrom(draftFrom);
    setCustomTo(draftTo);
    setDatePreset('custom');
  }, [draftFrom, draftTo]);

  const clearCustomRange = useCallback(() => {
    setDraftFrom('');
    setDraftTo('');
    setCustomFrom('');
    setCustomTo('');
    setDatePreset('all');
  }, []);

  // Clear all active filters at once (D1).
  const clearAllFilters = useCallback(() => {
    setMediaFilter('all');
    setDayNightFilter(null);
    setShowFavoritesOnly(false);
    setShowNewOnly(false);
    setOnThisDay(false);
    clearCustomRange();
  }, [clearCustomRange]);

  // Enable Apply only when the staged range differs from what's applied.
  const rangeDirty = draftFrom !== customFrom || draftTo !== customTo;
  const hasCustomRange = datePreset === 'custom' && (customFrom || customTo);

  // ── D1: Active filter chip labels ─────────────────────────────────────────

  const activeChips = useMemo(() => {
    const chips = [];
    if (mediaFilter !== 'all') {
      chips.push({
        id: 'media',
        label: mediaFilter === 'videos' ? 'Videos' : 'Pictures',
        onRemove: () => setMediaFilter('all'),
      });
    }
    if (dayNightFilter) {
      chips.push({
        id: 'daynight',
        label: dayNightFilter === 'night' ? 'Night only' : 'Day only',
        onRemove: () => setDayNightFilter(null),
      });
    }
    if (datePreset === '7d') chips.push({ id: 'date', label: 'Last 7 days', onRemove: () => applyPreset('all') });
    else if (datePreset === '30d') chips.push({ id: 'date', label: 'Last 30 days', onRemove: () => applyPreset('all') });
    else if (datePreset === 'year') chips.push({ id: 'date', label: 'This year', onRemove: () => applyPreset('all') });
    else if (datePreset === 'custom') {
      const parts = [];
      if (customFrom) parts.push(`from ${customFrom}`);
      if (customTo) parts.push(`to ${customTo}`);
      if (parts.length) {
        chips.push({ id: 'date', label: parts.join(' '), onRemove: clearCustomRange });
      }
    }
    // E1: Favorites chip.
    if (showFavoritesOnly) {
      chips.push({
        id: 'favorites',
        label: `FAVORITES (${favorites.size})`,
        onRemove: () => setShowFavoritesOnly(false),
      });
    }
    // E3: New-only chip.
    if (showNewOnly) {
      chips.push({
        id: 'new',
        label: `NEW (${newItemsSet.size})`,
        onRemove: () => setShowNewOnly(false),
      });
    }
    // D5: On-this-day chip.
    if (onThisDay) {
      const today = new Date();
      chips.push({
        id: 'onthisday',
        label: `ON THIS DAY (${today.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })})`,
        onRemove: () => setOnThisDay(false),
      });
    }
    return chips;
  }, [mediaFilter, dayNightFilter, datePreset, customFrom, customTo, applyPreset, clearCustomRange,
      showFavoritesOnly, favorites, showNewOnly, newItemsSet, onThisDay]);

  // ── Insights data computations ─────────────────────────────────────────────
  // All computed from allItems (respects active category + filters) or raw
  // categoryMap (for totals that should be filter-independent).

  // S1: Activity-by-hour heatmap. Bucket items by UTC hour (0–23), optionally
  // for a specific species (heatmapSpecies). Skips items with timestamp === 0.
  const insightsHourData = useMemo(() => {
    const counts = Array(24).fill(0);
    const sourceCats = heatmapSpecies
      ? [heatmapSpecies]
      : (activeCategory === 'all' ? categories : [activeCategory]);
    const { from, to } = dateWindow;

    for (const cat of sourceCats) {
      for (const entry of categoryMap[cat] || []) {
        const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
        if (!ts) continue;
        if (from !== null && ts < from) continue;
        if (to !== null && ts > to) continue;
        if (mediaFilter !== 'all') {
          const isVid = typeof entry === 'object' && entry?.type === 'video';
          if (mediaFilter === 'videos' && !isVid) continue;
          if (mediaFilter === 'pictures' && isVid) continue;
        }
        const hour = new Date(ts).getUTCHours();
        counts[hour]++;
      }
    }
    return counts;
  }, [categories, categoryMap, activeCategory, heatmapSpecies, dateWindow, mediaFilter]);

  const insightsPeakHour = useMemo(() => {
    const max = Math.max(...insightsHourData);
    if (max === 0) return null;
    return insightsHourData.indexOf(max);
  }, [insightsHourData]);

  // S2: Items-per-month bar chart, filtered by active category + media/date.
  const insightsMonthData = useMemo(() => {
    const buckets = {};
    const cats = activeCategory === 'all' ? categories : [activeCategory];
    const { from, to } = dateWindow;

    for (const cat of cats) {
      for (const entry of categoryMap[cat] || []) {
        const ts = (typeof entry === 'object' && entry?.timestamp) || 0;
        if (!ts) continue;
        if (from !== null && ts < from) continue;
        if (to !== null && ts > to) continue;
        if (mediaFilter !== 'all') {
          const isVid = typeof entry === 'object' && entry?.type === 'video';
          if (mediaFilter === 'videos' && !isVid) continue;
          if (mediaFilter === 'pictures' && isVid) continue;
        }
        const key = fmtMonthYear(ts);
        buckets[key] = (buckets[key] || 0) + 1;
      }
    }
    // Sort chronologically by actual date value (parse back).
    const sorted = Object.entries(buckets)
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => {
        // Re-parse for comparison: fmtMonthYear output is locale-dependent
        // but we can sort by the original timestamps instead.
        return a.key.localeCompare(b.key);
      });
    return sorted;
  }, [categories, categoryMap, activeCategory, dateWindow, mediaFilter]);

  // S3: Collection dashboard stats. Computed from filtered allItems.
  const insightsDashboard = useMemo(() => {
    if (allItems.length === 0) return null;

    let earliest = Infinity;
    let latest = -Infinity;
    let photoCount = 0;
    let videoCount = 0;
    let noTimestampCount = 0;
    const dayCounts = {}; // 'YYYY-MM-DD' → count

    for (const item of allItems) {
      const ts = item.timestamp || 0;
      if (!ts) { noTimestampCount++; continue; }
      if (ts < earliest) earliest = ts;
      if (ts > latest) latest = ts;
      const dayKey = new Date(ts).toISOString().slice(0, 10); // 'YYYY-MM-DD' UTC
      dayCounts[dayKey] = (dayCounts[dayKey] || 0) + 1;
      if (item.type === 'video') videoCount++;
      else photoCount++;
    }

    let busiestDay = null;
    let busiestCount = 0;
    for (const [day, cnt] of Object.entries(dayCounts)) {
      if (cnt > busiestCount) { busiestCount = cnt; busiestDay = day; }
    }

    return {
      total: allItems.length,
      photoCount,
      videoCount,
      noTimestampCount,
      earliest: earliest === Infinity ? null : earliest,
      latest: latest === -Infinity ? null : latest,
      busiestDay,
      busiestCount,
    };
  }, [allItems]);

  // S5: Day/night split across filtered allItems.
  const insightsDayNight = useMemo(() => {
    let day = 0, night = 0, unknown = 0;
    for (const item of allItems) {
      const ts = item.timestamp || 0;
      if (!ts) { unknown++; continue; }
      const hour = new Date(ts).getUTCHours();
      if (isNightHour(hour)) night++;
      else day++;
    }
    return { day, night, unknown };
  }, [allItems]);

  // ── Derived values ─────────────────────────────────────────────────────────

  const onSlide = (index) => setCurrentSlide(index);

  const show = allItems.length > 0;
  const atStart = currentSlide <= 0;
  const atEnd = currentSlide >= images.length - 1;
  const activeFilterCount =
    (mediaFilter !== 'all' ? 1 : 0) +
    (datePreset !== 'all' ? 1 : 0) +
    (dayNightFilter ? 1 : 0) +
    (showFavoritesOnly ? 1 : 0) +
    (showNewOnly ? 1 : 0) +
    (onThisDay ? 1 : 0);

  // Sh4: compare modal auto-opens when 2 items are selected.
  const compareModalOpen = compareMode && compareItems.length === 2;

  // ── Retry handler (G2) ─────────────────────────────────────────────────────

  const retryLoad = () => {
    setLoadError(false);
    setLoading(true);
    // Re-run the load by refreshing the page — simplest reliable approach.
    window.location.reload();
  };

  // ── Insights label for the current context ─────────────────────────────────
  const insightsContextLabel = useMemo(() => {
    if (activeCategory === 'all') return 'ALL CATEGORIES';
    return formatLabel(activeCategory).toUpperCase();
  }, [activeCategory]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="App" data-timeband={viewMode === 'single' && currentItemBand ? currentItemBand : undefined}>
      <header className="site-header">
        <div className="header-rule" />
        <h1 className="site-title">DOE & TELL</h1>
        <p className="site-subtitle">Trail Cam Gallery</p>
        <div className="header-rule" />
      </header>

      <main className="site-content">
        {/* G1: Skeleton grid during load (replaces bare spinner). */}
        {loading ? (
          <div className="loading-state">
            {/* Keep a minimal spinner for very short loads; skeleton handles the rest. */}
            <div
              className="loading-spinner"
              role="status"
              aria-label="Loading gallery"
            />
            <p>LOADING...</p>
            <SkeletonGrid />
          </div>
        ) : loadError ? (
          /* G2: Explicit error state with retry. */
          <div className="error-state">
            <p className="error-state-msg">COULDN'T LOAD PHOTOS</p>
            <p className="error-state-hint">Check your connection and try again.</p>
            <button
              type="button"
              className="toolbar-btn toolbar-btn-accent"
              onClick={retryLoad}
            >
              RETRY
            </button>
          </div>
        ) : (
          <>
            {categories.length > 0 && (
              <>
                {/* B1 / C1: On mobile render a single-row scrollable strip;
                    on desktop keep the existing wrapping tab layout. */}
                {isMobile ? (
                  <div className="category-strip-wrapper" aria-label="Categories">
                    <nav className="category-strip" aria-label="Category filter">
                      <button
                        className={`category-btn ${activeCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveCategory('all')}
                      >
                        ALL <span className="count">{filteredCounts.__all}</span>
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat}
                          className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                          onClick={() => setActiveCategory(cat)}
                        >
                          {formatLabel(cat).toUpperCase()} <span className="count">{filteredCounts[cat] ?? 0}</span>
                        </button>
                      ))}
                    </nav>
                  </div>
                ) : (
                  <nav className="category-tabs" aria-label="Category filter">
                    <button
                      className={`category-btn ${activeCategory === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('all')}
                    >
                      ALL <span className="count">{filteredCounts.__all}</span>
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
                        onClick={() => setActiveCategory(cat)}
                      >
                        {formatLabel(cat).toUpperCase()} <span className="count">{filteredCounts[cat] ?? 0}</span>
                      </button>
                    ))}
                  </nav>
                )}
              </>
            )}

            {categories.length > 0 && (
              <div className="controls-bar">
                {/* Left cluster: Filters toggle + Favorites toggle */}
                <div className="controls-bar-left">
                  <button
                    type="button"
                    className={`filters-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount ? 'has-active' : ''}`}
                    onClick={() => setFiltersOpen((o) => !o)}
                    aria-expanded={filtersOpen}
                  >
                    FILTERS{activeFilterCount ? ` (${activeFilterCount})` : ''}
                    <span className="caret">{filtersOpen ? '▴' : '▾'}</span>
                  </button>
                  {/* E1: Standalone favorites toggle for quick access */}
                  <button
                    type="button"
                    className={`filters-toggle ${showFavoritesOnly ? 'has-active' : ''}`}
                    onClick={() => setShowFavoritesOnly(v => !v)}
                    aria-pressed={showFavoritesOnly}
                  >
                    ♥ FAVORITES{favorites.size > 0 ? ` (${favorites.size})` : ''}
                  </button>
                </div>

                {/* Centre: Surprise Me — promoted from tiny toolbar icon to a
                    clearly labelled button as specified by A3 / H1. */}
                {show && allItems.length > 1 && viewMode !== 'insights' && viewMode !== 'game' && (
                  <button
                    type="button"
                    className="toolbar-btn toolbar-btn-accent surprise-btn"
                    onClick={goRandom}
                    aria-label="Surprise me — open a random item from the full collection"
                    title="Pick a random item from the full collection"
                  >
                    <IconShuffle />
                    SURPRISE ME
                  </button>
                )}

                {/* Right cluster: Sort (D2: labelled) + view-mode toggle */}
                <div className="controls-bar-right">
                  {/* D2: "SORT" label visually distinguishes this from date-filter buttons. */}
                  {viewMode !== 'insights' && viewMode !== 'game' && (
                    <div className="sort-group" role="group" aria-label="Sort direction">
                      <span className="sort-label" aria-hidden="true">SORT</span>
                      <div className="date-sort">
                        {[
                          { id: 'desc', label: 'NEWEST' },
                          { id: 'asc', label: 'OLDEST' },
                        ].map(({ id, label }) => (
                          <button
                            key={id}
                            className={`date-preset-btn ${sortDir === id ? 'active' : ''}`}
                            onClick={() => setSortDir(id)}
                            aria-pressed={sortDir === id}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Grid / Single / Insights view toggle (A1 + Batch 3a). */}
                  <div className="view-toggle" role="group" aria-label="View mode">
                    <button
                      type="button"
                      className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setAndPersistViewMode('grid')}
                      aria-pressed={viewMode === 'grid'}
                      aria-label="Grid view"
                      title="Grid view"
                    >
                      <IconGrid />
                    </button>
                    <button
                      type="button"
                      className={`view-toggle-btn ${viewMode === 'single' ? 'active' : ''}`}
                      onClick={() => setAndPersistViewMode('single')}
                      aria-pressed={viewMode === 'single'}
                      aria-label="Single view"
                      title="Single view"
                    >
                      <IconSingle />
                    </button>
                    <button
                      type="button"
                      className={`view-toggle-btn ${viewMode === 'insights' ? 'active' : ''}`}
                      onClick={() => setAndPersistViewMode('insights')}
                      aria-pressed={viewMode === 'insights'}
                      aria-label="Insights view"
                      title="Insights"
                    >
                      <IconInsights />
                    </button>
                    <button
                      type="button"
                      className={`view-toggle-btn ${viewMode === 'game' ? 'active' : ''}`}
                      onClick={() => { setAndPersistViewMode('game'); if (!gameItem) pickGameItem(); }}
                      aria-pressed={viewMode === 'game'}
                      aria-label="Guess the Time game"
                      title="Guess the Time"
                    >
                      <IconGame />
                    </button>
                  </div>
                  {/* Sh4: Compare mode toggle (grid only) */}
                  {viewMode === 'grid' && (
                    <button
                      type="button"
                      className={`view-toggle-btn ${compareMode ? 'active' : ''}`}
                      onClick={() => { setCompareMode(v => !v); setCompareItems([]); }}
                      aria-pressed={compareMode}
                      aria-label="Compare mode"
                      title="Compare two items"
                      style={{ borderLeft: '1px solid #2a2824', marginLeft: 4 }}
                    >
                      <IconCompare />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* D1: Active-filter chips + result count + clear-all. */}
            {activeChips.length > 0 && (
              <div className="active-filters-bar" aria-label="Active filters">
                <span className="result-count" aria-live="polite">
                  {allItems.length} {allItems.length === 1 ? 'result' : 'results'}
                </span>
                {activeChips.map(chip => (
                  <button
                    key={chip.id}
                    type="button"
                    className="filter-chip"
                    onClick={chip.onRemove}
                    aria-label={`Remove filter: ${chip.label}`}
                  >
                    {chip.label}
                    <span className="chip-x"><IconX /></span>
                  </button>
                ))}
                {activeChips.length > 1 && (
                  <button
                    type="button"
                    className="filter-chip filter-chip-clear"
                    onClick={clearAllFilters}
                    aria-label="Clear all filters"
                  >
                    CLEAR ALL
                  </button>
                )}
              </div>
            )}

            {categories.length > 0 && filtersOpen && (
              <div className="filters-panel">
                <div className="filter-group">
                  <span className="filter-group-label">Media</span>
                  <div className="media-filter" role="group" aria-label="Media type filter">
                    {[
                      { id: 'all', label: 'ALL' },
                      { id: 'pictures', label: 'PICTURES' },
                      { id: 'videos', label: 'VIDEOS' },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        className={`media-filter-btn ${mediaFilter === id ? 'active' : ''}`}
                        onClick={() => setMediaFilter(id)}
                        aria-pressed={mediaFilter === id}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* S5: Day/night quick filter inside the filters panel. */}
                <div className="filter-group">
                  <span className="filter-group-label">Time of day</span>
                  <div className="media-filter" role="group" aria-label="Time of day filter">
                    {[
                      { id: null, label: 'ALL' },
                      { id: 'day', label: 'DAY' },
                      { id: 'night', label: 'NIGHT' },
                    ].map(({ id, label }) => (
                      <button
                        key={String(id)}
                        className={`media-filter-btn ${dayNightFilter === id ? 'active' : ''}`}
                        onClick={() => setDayNightFilter(id)}
                        aria-pressed={dayNightFilter === id}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <span className="filter-group-label">Date</span>
                  <div className="date-presets" role="group" aria-label="Date range presets">
                    {[
                      { id: 'all', label: 'ALL DATES' },
                      { id: '7d', label: '7 DAYS' },
                      { id: '30d', label: '30 DAYS' },
                      { id: 'year', label: 'THIS YEAR' },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        className={`date-preset-btn ${datePreset === id ? 'active' : ''}`}
                        onClick={() => applyPreset(id)}
                        aria-pressed={datePreset === id}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <span className="filter-group-label">Custom range</span>
                  <div className="date-custom">
                    <label className="date-custom-label">
                      FROM
                      <input
                        type="date"
                        className="date-input"
                        value={draftFrom}
                        max={draftTo || undefined}
                        onChange={(e) => setDraftFrom(e.target.value)}
                      />
                    </label>
                    <label className="date-custom-label">
                      TO
                      <input
                        type="date"
                        className="date-input"
                        value={draftTo}
                        min={draftFrom || undefined}
                        onChange={(e) => setDraftTo(e.target.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="toolbar-btn toolbar-btn-accent"
                      onClick={applyCustomRange}
                      disabled={!rangeDirty}
                    >
                      APPLY
                    </button>
                    {hasCustomRange && (
                      <button type="button" className="toolbar-btn" onClick={clearCustomRange}>
                        CLEAR
                      </button>
                    )}
                  </div>
                </div>

                {/* D5: Quick filters group */}
                <div className="filter-group">
                  <span className="filter-group-label">Quick filters</span>
                  <div className="media-filter" role="group">
                    <button
                      className={`media-filter-btn ${onThisDay ? 'active' : ''}`}
                      onClick={() => setOnThisDay(v => !v)}
                      aria-pressed={onThisDay}
                    >
                      ON THIS DAY
                    </button>
                  </div>
                </div>

                {/* E1 + E3: Show filter group */}
                <div className="filter-group">
                  <span className="filter-group-label">Show</span>
                  <div className="media-filter" role="group">
                    <button
                      className={`media-filter-btn ${showFavoritesOnly ? 'active' : ''}`}
                      onClick={() => setShowFavoritesOnly(v => !v)}
                      aria-pressed={showFavoritesOnly}
                    >
                      FAVORITES{favorites.size > 0 ? ` (${favorites.size})` : ''}
                    </button>
                    {lastVisitTs && newItemsSet.size > 0 && (
                      <button
                        className={`media-filter-btn ${showNewOnly ? 'active' : ''}`}
                        onClick={() => setShowNewOnly(v => !v)}
                        aria-pressed={showNewOnly}
                      >
                        NEW ({newItemsSet.size})
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Gallery Container ──────────────────────────────────────── */}
            <div className="gallery-container">
              {flagToast && (
                <p className="flag-toast" role="status">
                  {flagToast}
                </p>
              )}

              {/* ── INSIGHTS VIEW (Batch 3a) ───────────────────────────── */}
              {viewMode === 'insights' && (
                <div className="insights-view">
                  <div className="insights-header">
                    <span className="insights-title">INSIGHTS</span>
                    <span className="insights-context">{insightsContextLabel}</span>
                  </div>

                  {allItems.length === 0 ? (
                    <div className="empty-state">
                      <p className="empty-state-msg">NO DATA FOR CURRENT FILTERS</p>
                      {activeFilterCount > 0 && (
                        <button
                          type="button"
                          className="toolbar-btn toolbar-btn-accent empty-state-action"
                          onClick={clearAllFilters}
                        >
                          CLEAR FILTERS
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* S3: Collection dashboard ─────────────────────── */}
                      {insightsDashboard && (
                        <section className="insights-section" aria-labelledby="ins-dash-title">
                          <h2 id="ins-dash-title" className="insights-section-title">BY THE NUMBERS</h2>
                          <div className="dashboard-grid">
                            <div className="dashboard-card">
                              <span className="dashboard-value">{insightsDashboard.total.toLocaleString()}</span>
                              <span className="dashboard-label">TOTAL ITEMS</span>
                            </div>
                            <div className="dashboard-card">
                              <span className="dashboard-value">{insightsDashboard.photoCount.toLocaleString()}</span>
                              <span className="dashboard-label">PHOTOS</span>
                            </div>
                            <div className="dashboard-card">
                              <span className="dashboard-value">{insightsDashboard.videoCount.toLocaleString()}</span>
                              <span className="dashboard-label">VIDEOS</span>
                            </div>
                            {insightsDashboard.earliest && (
                              <div className="dashboard-card">
                                <span className="dashboard-value dashboard-value-sm">{fmtFullDate(insightsDashboard.earliest)}</span>
                                <span className="dashboard-label">FIRST CAPTURE</span>
                              </div>
                            )}
                            {insightsDashboard.latest && (
                              <div className="dashboard-card">
                                <span className="dashboard-value dashboard-value-sm">{fmtFullDate(insightsDashboard.latest)}</span>
                                <span className="dashboard-label">LAST CAPTURE</span>
                              </div>
                            )}
                            {insightsDashboard.busiestDay && (
                              <div className="dashboard-card dashboard-card-accent">
                                <span className="dashboard-value dashboard-value-sm">{fmtFullDate(new Date(insightsDashboard.busiestDay + 'T00:00:00Z').getTime())}</span>
                                <span className="dashboard-label">BUSIEST DAY ({insightsDashboard.busiestCount} items)</span>
                              </div>
                            )}
                          </div>

                          {/* Per-species breakdown when "all" is active */}
                          {activeCategory === 'all' && categories.length > 0 && (
                            <div className="species-breakdown">
                              <p className="species-breakdown-title">PER SPECIES (filtered)</p>
                              <div className="species-breakdown-list">
                                {categories
                                  .filter(cat => filteredCounts[cat] > 0)
                                  .sort((a, b) => filteredCounts[b] - filteredCounts[a])
                                  .map(cat => {
                                    const count = filteredCounts[cat];
                                    const pct = insightsDashboard.total > 0
                                      ? Math.round((count / insightsDashboard.total) * 100)
                                      : 0;
                                    const isRare = rareCategories.has(cat);
                                    return (
                                      <div key={cat} className="species-row">
                                        <span className="species-row-name">
                                          {formatLabel(cat).toUpperCase()}
                                          {isRare && <span className="rare-badge" aria-label="Rare sighting">RARE</span>}
                                        </span>
                                        <div className="species-row-bar-wrap">
                                          <div
                                            className="species-row-bar"
                                            style={{ width: `${pct}%` }}
                                            aria-hidden="true"
                                          />
                                        </div>
                                        <span className="species-row-count">{count}</span>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </section>
                      )}

                      {/* S5: Day vs night split ──────────────────────── */}
                      <section className="insights-section" aria-labelledby="ins-daynight-title">
                        <h2 id="ins-daynight-title" className="insights-section-title">DAY VS NIGHT</h2>
                        <div className="daynight-summary">
                          <div className="daynight-block daynight-block-day">
                            <span className="daynight-value">{insightsDayNight.day}</span>
                            <span className="daynight-label">DAY</span>
                            <span className="daynight-sublabel">5am – 8pm</span>
                          </div>
                          <div className="daynight-divider" aria-hidden="true" />
                          <div className="daynight-block daynight-block-night">
                            <span className="daynight-value">{insightsDayNight.night}</span>
                            <span className="daynight-label">NIGHT</span>
                            <span className="daynight-sublabel">8pm – 5am</span>
                          </div>
                        </div>
                        {insightsDayNight.day + insightsDayNight.night > 0 && (
                          <div
                            className="daynight-bar-track"
                            role="img"
                            aria-label={`Day: ${insightsDayNight.day}, Night: ${insightsDayNight.night}`}
                          >
                            <div
                              className="daynight-bar-day"
                              style={{
                                width: `${Math.round((insightsDayNight.day / (insightsDayNight.day + insightsDayNight.night)) * 100)}%`,
                              }}
                            />
                          </div>
                        )}
                        {/* S5: Quick-filter buttons to narrow the grid/single view. */}
                        <div className="daynight-filter-row">
                          <span className="filter-group-label">QUICK FILTER</span>
                          <div className="media-filter" role="group" aria-label="Day/night quick filter">
                            {[
                              { id: null, label: 'ALL' },
                              { id: 'day', label: 'DAY ONLY' },
                              { id: 'night', label: 'NIGHT ONLY' },
                            ].map(({ id, label }) => (
                              <button
                                key={String(id)}
                                className={`media-filter-btn ${dayNightFilter === id ? 'active' : ''}`}
                                onClick={() => setDayNightFilter(id)}
                                aria-pressed={dayNightFilter === id}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          {dayNightFilter && (
                            <button
                              type="button"
                              className="toolbar-btn toolbar-btn-accent"
                              onClick={() => { setAndPersistViewMode('grid'); }}
                              title="Switch to grid view with this filter applied"
                            >
                              VIEW IN GRID
                            </button>
                          )}
                        </div>
                        {/* Nocturnal note for relevant categories */}
                        {(activeCategory === 'all' || ['coyote', 'raccoon', 'opossum'].includes(activeCategory)) && (
                          <p className="insights-note">
                            Coyotes, raccoons, and opossums are predominantly nocturnal — compare their night tallies above.
                          </p>
                        )}
                      </section>

                      {/* S1: Activity-by-hour heatmap ────────────────── */}
                      <section className="insights-section" aria-labelledby="ins-hour-title">
                        <h2 id="ins-hour-title" className="insights-section-title">ACTIVITY BY HOUR</h2>
                        {/* Species selector for the heatmap */}
                        {activeCategory === 'all' && categories.length > 1 && (
                          <div className="heatmap-species-row">
                            <span className="filter-group-label">SPECIES</span>
                            <div className="heatmap-species-select-wrap">
                              <select
                                className="insights-select"
                                value={heatmapSpecies || ''}
                                onChange={(e) => setHeatmapSpecies(e.target.value || null)}
                                aria-label="Select species for heatmap"
                              >
                                <option value="">All species</option>
                                {categories.map(cat => (
                                  <option key={cat} value={cat}>{formatLabel(cat)}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        <HourHeatmap
                          hourCounts={insightsHourData}
                          peakHour={insightsPeakHour}
                        />
                      </section>

                      {/* S2: Month trend chart ────────────────────────── */}
                      <section className="insights-section" aria-labelledby="ins-month-title">
                        <h2 id="ins-month-title" className="insights-section-title">MONTHLY TREND</h2>
                        <MonthBarChart
                          data={insightsMonthData}
                          label={insightsContextLabel}
                        />
                      </section>
                    </>
                  )}
                </div>
              )}

              {/* ── GUESS THE TIME GAME ───────────────────────────────── */}
              {viewMode === 'game' && (() => {
                const gameCorrectBand = gameItem?.timestamp
                  ? hourBand(new Date(gameItem.timestamp).getUTCHours())
                  : null;
                return (
                  <div className="game-view">
                    <div className="game-header">
                      <div>
                        <h2 className="game-title">GUESS THE TIME</h2>
                        <p className="game-subtitle">What time of day was this trail cam photo taken?</p>
                      </div>
                      <div className="game-streak-display">
                        <span className="game-streak-num">{gameStreak}</span>
                        <span className="game-streak-label">STREAK</span>
                        {gameBestStreak > 1 && (
                          <span className="game-best-label">BEST {gameBestStreak}</span>
                        )}
                      </div>
                    </div>

                    {gameItem ? (
                      <>
                        <div className="game-image-wrap">
                          <img
                            src={gameItem.display || gameItem.url}
                            alt="Trail camera photo — guess when it was taken"
                            className="game-image"
                          />
                          <div className="game-timestamp-mask" aria-hidden="true">
                            {!gameGuess
                              ? <span className="game-mask-text">TIMESTAMP HIDDEN</span>
                              : <span className="game-mask-revealed">
                                  {fmtCaptureTime(gameItem.timestamp)} · {gameCorrectBand?.toUpperCase()}
                                </span>
                            }
                          </div>
                        </div>

                        {!gameGuess ? (
                          <div className="game-choices">
                            <p className="game-prompt">WHEN WAS THIS TAKEN?</p>
                            <div className="game-band-btns">
                              {['night', 'dawn', 'morning', 'afternoon', 'dusk'].map(band => (
                                <button
                                  key={band}
                                  type="button"
                                  className="game-band-btn"
                                  style={{ '--band-color': BAND_INFO[band].accent }}
                                  onClick={() => handleGameGuess(band)}
                                >
                                  <span className="game-band-name">{BAND_INFO[band].label}</span>
                                  <span className="game-band-range">{BAND_INFO[band].range}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="game-reveal">
                            <p className={`game-result ${gameGuess === gameCorrectBand ? 'game-result-correct' : 'game-result-wrong'}`}>
                              {gameGuess === gameCorrectBand
                                ? gameStreak > 1 ? `✓ CORRECT — ${gameStreak} IN A ROW` : '✓ CORRECT'
                                : '✗ NOT QUITE'
                              }
                            </p>
                            <p className="game-actual">
                              Taken at <strong>{fmtCaptureTime(gameItem.timestamp)}</strong>
                              {' '}({BAND_INFO[gameCorrectBand]?.label || gameCorrectBand})
                              {gameGuess !== gameCorrectBand && (
                                <> — you guessed <span style={{ color: BAND_INFO[gameGuess]?.accent }}>{BAND_INFO[gameGuess]?.label}</span></>
                              )}
                            </p>
                            <button
                              type="button"
                              className="toolbar-btn toolbar-btn-accent game-next-btn"
                              onClick={pickGameItem}
                            >
                              NEXT PHOTO →
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="game-loading"><div className="loading-spinner" /></div>
                    )}
                  </div>
                );
              })()}

              {show && viewMode !== 'insights' && viewMode !== 'game' ? (
                <>
                  {/* ── GRID VIEW (A1 / A2 / D1 / D6) ───────────────────── */}
                  {viewMode === 'grid' && (
                    <div className="grid-view">
                      {/* Count indicator (A2). */}
                      <p className="grid-count" aria-live="polite">
                        SHOWING {Math.min(gridVisible, allItems.length)} OF {allItems.length}
                      </p>

                      {/* C3: Date-jump rail — year/month chips for quick scrolling. */}
                      {dateGroups.length > 1 && (
                        <div className="date-rail" aria-label="Jump to date">
                          {dateGroups.map(g => (
                            <button
                              key={g.key}
                              type="button"
                              className="date-rail-chip"
                              onClick={() => jumpToDate(g.firstIdx)}
                              title={`Jump to ${g.label}`}
                            >
                              {g.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Contact-sheet grid. */}
                      <div className="photo-grid" role="list">
                        {gridItems.map((item, idx) => {
                          const isRare = item.category ? rareCategories.has(item.category) : false;
                          const itemHour = item.timestamp ? new Date(item.timestamp).getUTCHours() : null;
                          const itemIsNight = itemHour !== null ? isNightHour(itemHour) : false;
                          const isFaved = favorites.has(item.sourceUrl);
                          const isNew = newItemsSet.has(item.sourceUrl);
                          // Sh4: compare selection index (1 or 2 when selected)
                          const compareSelIdx = compareMode
                            ? compareItems.findIndex(i => i.sourceUrl === item.sourceUrl)
                            : -1;
                          return (
                            <button
                              key={`${item.sourceUrl}-${idx}`}
                              type="button"
                              className={`grid-tile${compareMode && compareSelIdx !== -1 ? ' grid-tile-selected' : ''}`}
                              role="listitem"
                              onClick={() => {
                                if (compareMode) {
                                  setCompareItems(prev => {
                                    const selIdx2 = prev.findIndex(i => i.sourceUrl === item.sourceUrl);
                                    if (selIdx2 !== -1) return prev.filter((_, i) => i !== selIdx2);
                                    if (prev.length >= 2) return prev;
                                    return [...prev, item];
                                  });
                                } else {
                                  openGridItem(idx);
                                }
                              }}
                              // F1: store a ref to each tile for focus-restoration.
                              ref={(el) => {
                                if (el) gridTileRefs.current[idx] = el;
                                else delete gridTileRefs.current[idx];
                              }}
                              aria-label={`${item.category ? formatLabel(item.category) : 'Photo'}${item.timestamp ? ', ' + formatDate(item.timestamp) : ''}${item.type === 'video' ? ' (video)' : ''}${isRare ? ', rare sighting' : ''}${isFaved ? ', favorited' : ''}`}
                            >
                              {/* Use the tiny thumbnail rendition — not display/url —
                                  to stay cheap. loading="lazy" defers off-screen fetches. */}
                              <img
                                src={item.thumbnail}
                                // F2: Real alt derived from category + date (empty for
                                // decorative grid tiles since the button has aria-label).
                                alt=""
                                loading="lazy"
                                className="grid-tile-img"
                              />
                              {/* Video play affordance overlay. */}
                              {item.type === 'video' && (
                                <span className="grid-tile-play" aria-hidden="true">
                                  <IconPlay />
                                </span>
                              )}
                              {/* Category badge in "all" view — reuses the badge pattern. */}
                              {item.category && activeCategory === 'all' && (
                                <span className="grid-tile-badge">
                                  {formatLabel(item.category).toUpperCase()}
                                </span>
                              )}
                              {/* E5: Rare sighting badge */}
                              {isRare && (
                                <span className="grid-tile-rare" aria-hidden="true">RARE</span>
                              )}
                              {/* S5: Night badge — subtle indicator for nocturnal captures */}
                              {itemIsNight && item.timestamp ? (
                                <span className="grid-tile-night" aria-hidden="true">NIGHT</span>
                              ) : null}
                              {/* E1: Heart / favorite button */}
                              <button
                                type="button"
                                className={`grid-tile-heart${isFaved ? ' grid-tile-heart-active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); toggleFavorite(item.sourceUrl); }}
                                aria-label={isFaved ? 'Remove from favorites' : 'Add to favorites'}
                                title={isFaved ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <IconHeart filled={isFaved} />
                              </button>
                              {/* E3: NEW badge */}
                              {isNew && (
                                <span className="grid-tile-new" aria-hidden="true">NEW</span>
                              )}
                              {/* Sh4: Compare selection badge */}
                              {compareMode && compareSelIdx !== -1 && (
                                <span className="grid-tile-compare-badge" aria-hidden="true">
                                  {compareSelIdx + 1}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Sentinel element watched by IntersectionObserver for
                          auto-loading (A2). Hidden when all items are visible. */}
                      {gridVisible < allItems.length && (
                        <div
                          ref={gridSentinelRef}
                          className="grid-sentinel"
                          aria-hidden="true"
                        />
                      )}

                      {/* Visible "load more" fallback (also satisfies A2). */}
                      {gridVisible < allItems.length && (
                        <button
                          type="button"
                          className="toolbar-btn load-more-btn"
                          onClick={() => setGridVisible((v) => Math.min(v + PAGE_SIZE, allItems.length))}
                        >
                          LOAD MORE ({allItems.length - gridVisible} remaining)
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── SINGLE / CAROUSEL VIEW ────────────────────────────── */}
                  {viewMode === 'single' && (
                    // F1: tabIndex="-1" makes the container programmatically focusable
                    // so focus can land here when transitioning from grid.
                    <div
                      {...handlers}
                      className="single-view"
                      ref={singleViewRef}
                      tabIndex="-1"
                    >
                      {/* Back-to-grid button. */}
                      <button
                        type="button"
                        className="back-to-grid-btn toolbar-btn"
                        onClick={returnToGrid}
                        aria-label="Return to grid view"
                      >
                        ← BACK TO GRID
                      </button>

                      <ImageGallery
                        ref={galleryRef}
                        // Key on category + page; changing either forces a clean
                        // mount so the gallery's internal index stays in sync.
                        key={`${activeCategory}-${safePage}`}
                        items={images}
                        startIndex={currentSlide}
                        onSlide={onSlide}
                        onClick={onImageClick}
                        showNav={!isMobile}
                        showBullets={false}
                        showThumbnails={true}
                        showFullscreenButton={false}
                        showPlayButton={false}
                        autoPlay={false}
                        lazyLoad
                        slideInterval={3000}
                        additionalClass="custom-gallery"
                      />

                      {/* E3: "Tap to zoom" hint for touch users — shown only on touch
                          devices when the current slide is a photo (not a video). */}
                      {isMobile && images[currentSlide] && images[currentSlide].type !== 'video' && (
                        <p className="tap-zoom-hint" aria-hidden="true">
                          TAP PHOTO TO OPEN FULL SIZE
                        </p>
                      )}

                      <div className="gallery-toolbar icon-toolbar">
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={goPrev}
                          disabled={atStart}
                          aria-label="Previous image"
                          title="Previous"
                        >
                          <IconPrev />
                        </button>
                        <span className="toolbar-index">
                          {pageStart + currentSlide + 1} / {allItems.length}
                          {images[currentSlide]?.timestamp ? (
                            <span className="toolbar-date">{formatDate(images[currentSlide].timestamp)}</span>
                          ) : null}
                          {/* S4 / H3: Time-of-day context for the current item. */}
                          {currentItemHour !== null && currentItemBand && images[currentSlide]?.timestamp ? (
                            <span className="toolbar-time-of-day">
                              {fmtCaptureTime(images[currentSlide].timestamp)} · {currentItemBand}
                            </span>
                          ) : null}
                        </span>
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={goNext}
                          disabled={atEnd}
                          aria-label="Next image"
                          title="Next"
                        >
                          <IconNext />
                        </button>
                        <span className="toolbar-divider" aria-hidden="true" />
                        {/* E5: Rare badge in single view */}
                        {currentItemIsRare && (
                          <span className="toolbar-rare-badge" aria-label="Rare sighting">RARE</span>
                        )}
                        {/* E1: Favorite toggle in single view */}
                        <button
                          type="button"
                          className={`icon-btn ${currentItem && favorites.has(currentItem.sourceUrl) ? 'icon-btn-fav-active' : ''}`}
                          onClick={() => currentItem && toggleFavorite(currentItem.sourceUrl)}
                          aria-label={currentItem && favorites.has(currentItem.sourceUrl) ? 'Remove from favorites' : 'Add to favorites'}
                          title="Favorite"
                        >
                          <IconHeart filled={!!(currentItem && favorites.has(currentItem.sourceUrl))} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={viewFullSize}
                          aria-label="Open full-size image"
                          title="Open full-size image"
                        >
                          <IconExpand />
                        </button>
                        {/* Sh3: Download button (photos only; best-effort) */}
                        {currentItem && currentItem.type !== 'video' && (
                          <a
                            className="icon-btn"
                            href={currentItem.sourceUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download original photo"
                            title="Download photo"
                          >
                            <IconDownload />
                          </a>
                        )}
                        {/* Sh2: Share card button (photos only) */}
                        {currentItem && currentItem.type !== 'video' && (
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={() => openShareCard(currentItem)}
                            aria-label="Create shareable card"
                            title="Share card"
                          >
                            <IconShare />
                          </button>
                        )}
                        <button
                          type="button"
                          className={`icon-btn ${copied ? 'icon-btn-success' : ''}`}
                          onClick={copyImageLink}
                          aria-label={copied ? 'Link copied' : 'Copy image link'}
                          title={copied ? 'Copied!' : 'Copy link'}
                        >
                          {copied ? <IconCheck /> : <IconLink />}
                        </button>
                        {/* C2/Sh1: Copy view link (includes current item in URL) */}
                        <button
                          type="button"
                          className="toolbar-btn toolbar-btn-sm"
                          onClick={copyViewLink}
                          aria-label={copiedLink ? 'Link copied' : 'Copy view link'}
                          title={copiedLink ? 'Copied!' : 'Copy link to this view'}
                        >
                          {copiedLink ? 'COPIED!' : 'COPY LINK'}
                        </button>
                        {/* F5: ref for focus-return after modal close. */}
                        <button
                          type="button"
                          className="icon-btn icon-btn-warn"
                          onClick={openFlagModal}
                          aria-label="Report wrong category"
                          title="Wrong category?"
                          ref={flagBtnRef}
                        >
                          <IconFlag />
                        </button>
                      </div>

                      {/* Page navigation (single view only; no dead controls in grid). */}
                      {totalPages > 1 && (
                        <div className="pagination-bar">
                          <button
                            type="button"
                            className="toolbar-btn"
                            onClick={goPrevPage}
                            disabled={safePage <= 0}
                            aria-label="Previous page"
                          >
                            &laquo; PAGE
                          </button>
                          <span className="toolbar-index">
                            PAGE {safePage + 1} / {totalPages}
                          </span>
                          <button
                            type="button"
                            className="toolbar-btn"
                            onClick={goNextPage}
                            disabled={safePage >= totalPages - 1}
                            aria-label="Next page"
                          >
                            PAGE &raquo;
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : viewMode !== 'insights' && viewMode !== 'game' ? (
                /* D3: Empty state must offer at least one action to recover. */
                <div className="empty-state">
                  <p className="empty-state-msg">
                    NO IMAGES FOUND{activeCategory !== 'all' ? ` FOR ${formatLabel(activeCategory).toUpperCase()}` : ''}
                  </p>
                  {/* Primary recovery: clear filters if any are active. */}
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      className="toolbar-btn toolbar-btn-accent empty-state-action"
                      onClick={clearAllFilters}
                    >
                      CLEAR FILTERS
                    </button>
                  )}
                  {/* Secondary: switch to "all" if on a specific category. */}
                  {activeCategory !== 'all' && (
                    <button
                      type="button"
                      className="toolbar-btn empty-state-action"
                      onClick={() => setActiveCategory('all')}
                    >
                      BACK TO ALL
                    </button>
                  )}
                  {/* Fallback when already on "all" with no filters — shouldn't
                      normally happen, but avoids a dead end. */}
                  {activeCategory === 'all' && activeFilterCount === 0 && (
                    <p className="empty-state-hint">No items match the current view.</p>
                  )}
                </div>
              ) : null}
            </div>

            {/* ── Sh2: Share card modal ──────────────────────────────────── */}
            {shareCardOpen && shareCardItem && (
              <ShareCardModal
                item={shareCardItem}
                onClose={() => { setShareCardOpen(false); setShareCardItem(null); }}
              />
            )}

            {/* ── Sh4: Compare modal (auto-opens when 2 items selected) ──── */}
            {compareModalOpen && (
              <div className="flag-modal-backdrop" role="presentation" onClick={() => setCompareItems([])}>
                <div
                  className="compare-modal"
                  role="dialog"
                  aria-labelledby="compare-title"
                  aria-modal="true"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 id="compare-title" className="flag-modal-title">COMPARE</h2>
                  <div className="compare-grid">
                    {compareItems.map((item, i) => (
                      <div key={item.sourceUrl} className="compare-panel">
                        <img
                          src={item.original}
                          alt={item.originalAlt}
                          className="compare-img"
                        />
                        <div className="compare-meta">
                          <span className="compare-label">
                            {item.category ? formatLabel(item.category).toUpperCase() : 'PHOTO'}
                          </span>
                          {item.timestamp ? (
                            <span className="compare-date">{formatDate(item.timestamp)}</span>
                          ) : null}
                          {item.timestamp ? (
                            <span className="compare-time">
                              {fmtCaptureTime(item.timestamp)} · {hourBand(new Date(item.timestamp).getUTCHours())}
                            </span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => setCompareItems(prev => prev.filter((_, j) => j !== i))}
                        >
                          REMOVE
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flag-modal-actions">
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => { setCompareItems([]); setCompareMode(false); }}
                    >
                      EXIT COMPARE
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={() => setCompareItems([])}
                    >
                      PICK AGAIN
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Flag modal ─────────────────────────────────────────────── */}
            {flagModalOpen && (
              <div
                className="flag-modal-backdrop"
                role="presentation"
                onClick={closeFlagModal}
              >
                {/* F5: ref wires up the focus trap; Escape handled by useEffect above. */}
                <div
                  className="flag-modal"
                  role="dialog"
                  aria-labelledby="flag-modal-title"
                  aria-modal="true"
                  onClick={(e) => e.stopPropagation()}
                  ref={flagModalRef}
                >
                  <h2 id="flag-modal-title" className="flag-modal-title">
                    Wrong category?
                  </h2>
                  <p className="flag-modal-hint">
                    Optionally pick the category you think fits, or leave a short note. Submitting sends this image for review.
                  </p>
                  <label className="flag-modal-label" htmlFor="flag-suggested">
                    Should be
                  </label>
                  <select
                    id="flag-suggested"
                    className="flag-modal-select"
                    value={flagSuggested}
                    onChange={(e) => setFlagSuggested(e.target.value)}
                    disabled={flagSubmitting}
                  >
                    <option value="">Not sure / skip</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {formatLabel(cat)}
                      </option>
                    ))}
                  </select>
                  <label className="flag-modal-label" htmlFor="flag-note">
                    Note (optional)
                  </label>
                  <textarea
                    id="flag-note"
                    className="flag-modal-textarea"
                    rows={3}
                    maxLength={1000}
                    value={flagNote}
                    onChange={(e) => setFlagNote(e.target.value)}
                    disabled={flagSubmitting}
                    placeholder="e.g. looks more like a coyote"
                  />
                  {flagError && <p className="flag-modal-error">{flagError}</p>}
                  <div className="flag-modal-actions">
                    <button
                      type="button"
                      className="toolbar-btn"
                      onClick={closeFlagModal}
                      disabled={flagSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="toolbar-btn toolbar-btn-accent"
                      onClick={submitFlag}
                      disabled={flagSubmitting}
                    >
                      {flagSubmitting ? 'Sending…' : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="site-footer">
        <p>&copy; {new Date().getFullYear()} DOE &amp; TELL</p>
      </footer>
    </div>
  );
}

export default App;
